import { z } from "zod";
import { accreditationListQuery } from "./accreditation";
import type { AccreditationListItem } from "./accreditation-records";
import type { CardListItem } from "./e-accreditation";
import { cardsListQuery } from "./e-accreditation";
import type { HelpdeskTicketListItem } from "./helpdesk";
import { listHelpdeskTicketsQuery } from "./helpdesk";
import type { RoomingPerson } from "./accommodation";
import { roomingPeopleCsvQuery } from "./accommodation";

/**
 * Central Report Engine (Overview menu, staged at Sector 4 for a NOC; after
 * SUKMA's, user 2026-11-02). PDFs are drawn in the browser; the API hands
 * each report its rows.
 *
 * Rule 0: a report reads its rows in ONE request through `/reports/*`,
 * which walks the module's own list SQL (scope included) in keyset chunks of
 * `REPORT_CHUNK` on the server, stopped at `REPORT_ROWS_MAX` (`truncated`
 * says there were more — the PDF prints it). The 100-row list pages would
 * have cost 50 round trips for 5,000 cards. Counts stay single aggregates.
 */

/** Rows a single report may print. A NOC's whole delegation fits; a host narrows by filter. */
export const REPORT_ROWS_MAX = 5000;
/** Rows per statement while a report walks its list on the server. */
export const REPORT_CHUNK = 1000;

export const REPORT_ORGANISATION_NAME_MAX = 120;
export const REPORT_FOOTER_TEXT_MAX = 160;
export const REPORT_REASON_MAX = 500;

/** Every report endpoint answers this shape. */
export interface ReportRows<T> {
	items: T[];
	/** More rows matched than `REPORT_ROWS_MAX`; the report prints only the first. */
	truncated: boolean;
}

// ── Setup (the `report_settings` singleton, 0078) ───────────────────────────

/** `GET /reports/settings` — printed on every report. Any GMS role reads it. */
export interface ReportSettings {
	organisation_name: string;
	footer_text: string;
	updated_at: string;
}

/** The seed of 0078; also what a missing row reads as. */
export const REPORT_SETTINGS_DEFAULT = {
	organisation_name: "SEA Games Malaysia 2027",
	footer_text: "Generated by WSL GMS Central Report Engine",
} as const;

/** `PUT /reports/settings` (SUPER_ADMIN) — with a reason, the audit headline. */
export const putReportSettingsBody = z.object({
	organisation_name: z.string().trim().min(1).max(REPORT_ORGANISATION_NAME_MAX),
	footer_text: z.string().trim().max(REPORT_FOOTER_TEXT_MAX),
	reason: z.string().trim().min(1).max(REPORT_REASON_MAX),
});
export type PutReportSettingsBody = z.input<typeof putReportSettingsBody>;

// ── Accreditation ───────────────────────────────────────────────────────────

/**
 * `GET /reports/accreditation/people` — the records list's filters (no
 * paging, no sort: name order), so a report means exactly the rows the
 * records page shows for the same filters.
 */
export const reportAccreditationPeopleQuery = accreditationListQuery.omit({ cursor: true, limit: true, sort: true });
export type ReportAccreditationPeopleQuery = z.input<typeof reportAccreditationPeopleQuery>;

/** A records row as a report prints it: no photo URL, plus the ID number (the CSV export's rule). */
export type ReportPersonRow = Omit<AccreditationListItem, "photo_thumb_url" | "journey"> & {
	identification_number: string | null;
};

/** `GET /reports/accreditation/athletes-by-sport` — APPROVED athletes (category `Aa`). */
export const reportAthletesBySportQuery = z.object({
	games_id: z.string().uuid(),
	responsible_organization_id: z.string().uuid().optional(),
});
export type ReportAthletesBySportQuery = z.input<typeof reportAthletesBySportQuery>;

/**
 * One (sport, organisation) cell, counted in ONE aggregate. An athlete with
 * several sports counts once under each (`sport_id` ∪ `sport_ids`).
 */
export interface ReportAthletesBySportRow {
	sport: { id: string; code: string; name: string };
	organization: { id: string; code: string | null; name: string };
	women: number;
	men: number;
	/** No gender on record. */
	unknown: number;
	total: number;
}

// ── E-Accreditation ─────────────────────────────────────────────────────────

/** `GET /reports/cards` — the cards list's filters, no paging. */
export const reportCardsQuery = cardsListQuery.omit({ cursor: true, limit: true });
export type ReportCardsQuery = z.input<typeof reportCardsQuery>;
export type ReportCardRow = Omit<CardListItem, "photo_url">;

// ── Helpdesk ────────────────────────────────────────────────────────────────

/** `GET /reports/helpdesk/tickets` — the ticket list's filters, newest first, no paging. */
export const reportHelpdeskTicketsQuery = listHelpdeskTicketsQuery.omit({ cursor: true, limit: true, sort: true });
export type ReportHelpdeskTicketsQuery = z.input<typeof reportHelpdeskTicketsQuery>;
export type ReportTicketRow = Omit<HelpdeskTicketListItem, "can_edit" | "last_note">;

// ── Accommodation ───────────────────────────────────────────────────────────

/** `GET /reports/accommodation/rooming/:orgId` — one contingent's rooming list (the CSV's filters and cap). */
export const reportRoomingQuery = roomingPeopleCsvQuery;
export type ReportRoomingQuery = z.input<typeof reportRoomingQuery>;
export type ReportRoomingRow = Omit<RoomingPerson, "photo_thumb_url">;
